import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BORDER_RADIUS, SPACING_ELEMENT, SPACING_SECTION, SPACING_CARD_BODY, SPACING_CARD, SPACING_BUTTON, SPACING_COMPONENT, SPACING_GROUP } from '../constants/layout';
import {
  FONT_TITLE,
  FONT_TITLE_MOBILE,
  FONT_TITLE3,
  FONT_BODY,
  FONT_FOOTNOTE,
} from '../constants/typography';

export default function PrivacyPolicyView() {
  useEffect(() => {
    document.title = 'Privacy Policy - Koral Krush';
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="legal-page">
        <article className="legal-article">
          <header className="legal-header">
            <h1 className="legal-title">Privacy Policy</h1>
            <p className="legal-date">Effective date: April 6, 2026</p>
          </header>

          <div className="legal-body">
            <p>
              Koral Krush ("we," "us," or "our") operates the Koral Krush website and services (the "Service"). This Privacy Policy explains what information we collect, how we use it, who we share it with, and the choices you have. By using the Service, you agree to the practices described below.
            </p>

            <h2>1. Information You Provide</h2>

            <p><strong>Account information.</strong> When you create an account, we collect your email address and password. You may optionally add or update your first name, last name, phone number, and profile photo.</p>

            <p><strong>Order and checkout information.</strong> When you place an order, we collect your name, email address, and phone number. If you designate someone else to pick up your order, we collect that person's name, phone number, and email. We also collect any custom fulfillment instructions you provide.</p>

            <p><strong>Payment information.</strong> Payment card details are collected and processed entirely by our third-party payment processor. We never receive, handle, or store your card number, expiration date, or CVV. We do store an internal customer identifier that links your account to your payment profile, as well as payment status and transaction identifiers for order records.</p>

            <p><strong>Reviews.</strong> If you leave a product review, we collect the rating, title, body text, and any images you upload (up to five). Your first name is displayed publicly alongside your review.</p>

            <p><strong>Contact form.</strong> If you contact us through the website, we collect your name, email, phone number, and message content.</p>

            <h2>2. Information Collected Automatically</h2>

            <p><strong>Page view analytics.</strong> We collect basic analytics data when you browse the Service, including the page visited, product viewed (if applicable), browser user agent, referring URL, screen width, and timestamp. Each visitor is assigned an anonymous visitor identifier stored in your browser's local storage and a session identifier stored in session storage for the duration of your browsing session. Page view records are retained on our servers for 90 days and then expire. This data is used to understand how the Service is used and is not linked to your account.</p>

            <p><strong>Error logging.</strong> If an error occurs during checkout, we may log diagnostic information such as the error message, the step in the checkout process where it occurred, and your session identifier. This data is used solely to identify and fix technical issues.</p>

            <p><strong>Authentication data.</strong> Our authentication system uses cookies and tokens to maintain your login session and secure your account.</p>

            <h2>3. Local Storage and Session Storage</h2>
            <p>We store the following data locally in your browser:</p>
            <ul>
              <li><strong>Shopping cart:</strong> your cart contents are stored in local storage so they persist between visits. Cart data is not synced to our servers until you check out.</li>
              <li><strong>Visitor ID:</strong> an anonymous identifier for page view analytics, stored in local storage until you clear your browser data.</li>
              <li><strong>Session ID:</strong> an anonymous session identifier for analytics, stored in session storage and cleared when you close the browser.</li>
              <li><strong>Theme preference:</strong> your chosen display theme (light or dark mode).</li>
              <li><strong>Store details:</strong> cached store configuration to reduce load times.</li>
              <li><strong>Display and layout preferences:</strong> your preferences for how products and navigation are displayed.</li>
            </ul>

            <h2>4. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Process, fulfill, and manage your orders</li>
              <li>Send transactional emails, including order confirmations, ready-for-pickup notifications, refund confirmations, and cancellation notices</li>
              <li>Create and manage your account</li>
              <li>Respond to your questions and contact form submissions</li>
              <li>Analyze page views and site usage to improve the Service</li>
              <li>Log and diagnose errors during checkout to improve reliability</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2>5. Third-Party Services</h2>
            <p>We work with trusted third-party service providers to operate the Service. These providers process data only as necessary to perform their function:</p>
            <ul>
              <li><strong>Payment processing:</strong> all payments are handled by a PCI DSS compliant payment processor. Your card details are collected and stored by the processor directly and never touch our servers.</li>
              <li><strong>Cloud infrastructure:</strong> we use cloud services for authentication, data storage, and hosting.</li>
              <li><strong>Email delivery:</strong> a third-party email service sends transactional emails on our behalf, such as order confirmations, status updates, and refund notifications.</li>
            </ul>
            <p>We do not sell your personal information to anyone.</p>
            <p>We may disclose your information if required by law, legal process, or to protect the rights, property, or safety of Koral Krush, our users, or the public.</p>

            <h2>6. Data Security</h2>
            <p>We take reasonable measures to protect your information:</p>
            <ul>
              <li>All data is transmitted over HTTPS</li>
              <li>Passwords are handled entirely by our authentication provider and are never stored on or transmitted to our servers</li>
              <li>Payment card data is handled by our payment processor. We only store the last four digits of your card number for order identification purposes.</li>
              <li>Access to user data is restricted through database security rules</li>
            </ul>
            <p>No system is perfectly secure. We cannot guarantee absolute security, but we work to protect your data using industry-standard practices.</p>

            <h2>7. Data Retention</h2>
            <ul>
              <li><strong>Account data:</strong> retained for as long as your account is active.</li>
              <li><strong>Order records:</strong> retained indefinitely for business and legal purposes. If you delete your account, order records are anonymized (your name, email, and phone are removed) but the order itself is preserved.</li>
              <li><strong>Page view data:</strong> automatically expires after 90 days.</li>
              <li><strong>Contact submissions:</strong> retained until manually removed.</li>
              <li><strong>Reviews:</strong> retained indefinitely unless you request removal.</li>
            </ul>

            <h2>8. Account Deletion</h2>
            <p>
              You can delete your account at any time from your account settings. When you delete your account, we permanently remove your profile information, bookmarks, profile photo, payment customer record, and authentication credentials. Your past orders are anonymized: identifying information is stripped, but the order record is retained for our business records. Account deletion is irreversible.
            </p>

            <h2>9. Your Rights</h2>
            <p>Depending on where you live, you may have the right to:</p>
            <ul>
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your account and personal data</li>
            </ul>
            <p>You can update your name, email, and phone number directly from your account settings. You can delete your account at any time (see Section 8). For any other requests, email us at{' '}<a href="mailto:privacy@koralkrush.com">privacy@koralkrush.com</a>.</p>

            <h2>10. Children's Privacy</h2>
            <p>
              The Service is not directed at children under 13 and we do not knowingly collect personal information from children under 13. If we learn that we have collected data from a child under 13, we will delete it promptly. If you believe a child has provided us with personal information, please contact us.
            </p>

            <h2>11. Third-Party Links</h2>
            <p>
              The Service may link to third-party websites. We are not responsible for their privacy practices and encourage you to review their policies.
            </p>

            <h2>12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we make changes, we will update the effective date at the top of this page. Your continued use of the Service after a change takes effect means you accept the updated policy.
            </p>

            <h2>13. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, contact us at{' '}
              <a href="mailto:privacy@koralkrush.com">privacy@koralkrush.com</a>.
            </p>

            <div className="legal-separator" />

            <p className="legal-related">
              See also: <Link to="/terms">Terms of Service</Link>
            </p>
          </div>
        </article>
      </div>

      <style jsx>{`
        .legal-page {
          background: var(--color-background);
          padding: ${SPACING_SECTION} ${SPACING_CARD_BODY} 96px;
          min-height: 60vh;
        }

        @supports (padding: max(0px)) {
          .legal-page {
            padding-left: max(${SPACING_CARD_BODY}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD_BODY}, env(safe-area-inset-right));
          }
        }

        .legal-article {
          max-width: 680px;
          margin: 0 auto;
        }

        .legal-header {
          margin-bottom: ${SPACING_SECTION};
        }

        .legal-title {
          font-size: ${FONT_TITLE};
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 ${SPACING_ELEMENT};
          letter-spacing: -0.02em;
        }

        .legal-date {
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
          margin: 0;
        }

        .legal-body h2 {
          font-size: ${FONT_TITLE3};
          font-weight: 600;
          color: var(--color-text-primary);
          margin: ${SPACING_SECTION} 0 ${SPACING_COMPONENT};
        }

        .legal-body p {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          line-height: 1.75;
          margin: 0 0 ${SPACING_BUTTON};
        }

        .legal-body ul {
          margin: 0 0 ${SPACING_BUTTON};
          padding-left: ${SPACING_CARD_BODY};
          color: var(--color-text-secondary);
        }

        .legal-body li {
          font-size: ${FONT_BODY};
          line-height: 1.75;
          margin-bottom: 4px;
        }

        .legal-body a {
          color: var(--color-accent);
          text-decoration: none;
        }

        .legal-body a:hover {
          text-decoration: underline;
        }

        .legal-separator {
          border: none;
          border-top: 1px solid var(--color-border);
          margin: ${SPACING_SECTION} 0 ${SPACING_CARD_BODY};
        }

        .legal-related {
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
        }

        @media (max-width: 600px) {
          .legal-page {
            padding: ${SPACING_GROUP} ${SPACING_BUTTON} 64px;
          }

          .legal-title {
            font-size: ${FONT_TITLE_MOBILE};
          }

          .legal-header {
            margin-bottom: ${SPACING_CARD};
          }

          .legal-body h2 {
            margin-top: ${SPACING_GROUP};
          }
        }
      `}</style>
    </>
  );
}
